import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { DollarSign, TrendingUp, Calendar } from 'lucide-react';

interface InvestorCardProps {
  id: string;
  name: string;
  email: string;
  investedAmount: number;
  currentProfit: number;
  roi: number;
  joinDate: string;
  status: 'active' | 'inactive' | 'pending';
  onView?: () => void;
  onEdit?: () => void;
}

const InvestorCard = ({
  name,
  email,
  investedAmount,
  currentProfit,
  roi,
  joinDate,
  status,
  onView,
  onEdit,
}: InvestorCardProps) => {
  const getStatusColor = () => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{name}</CardTitle>
            <CardDescription className="mt-1">{email}</CardDescription>
          </div>
          <Badge className={getStatusColor()}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-blue-600" />
            <div>
              <p className="text-xs text-muted-foreground">Invested</p>
              <p className="text-sm font-semibold">
                ${investedAmount.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-600" />
            <div>
              <p className="text-xs text-muted-foreground">Current Profit</p>
              <p
                className={`text-sm font-semibold ${currentProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
              >
                ${currentProfit.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-purple-600" />
            <div>
              <p className="text-xs text-muted-foreground">ROI</p>
              <p className="text-sm font-semibold text-purple-600">
                {roi.toFixed(2)}%
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-orange-600" />
            <div>
              <p className="text-xs text-muted-foreground">Joined</p>
              <p className="text-sm font-semibold">
                {new Date(joinDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={onView}
          >
            View Details
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={onEdit}
          >
            Manage
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvestorCard;
